// Featured Events upgrade — shared admin media card for the event types that
// previously lacked flyer support: Court Movement sessions + Singles ladders.
//
// Bundles: primary flyer upload/replace/remove, a "Feature on Home" toggle,
// and the additional-photos gallery manager — all via /api/events/{kind}/...

import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Star } from '@phosphor-icons/react';
import EventFlyerUpload from './EventFlyerUpload';
import EventPhotoManager from './EventPhotoManager';
import { eventMediaAPI } from '../../lib/eventMediaApi';

export default function EventMediaCard({
  kind,                 // 'session' | 'ladder'
  eventId,
  eventName,
  flyerUrl,
  featuredOnHome = false,
  onChange,             // called after flyer/featured changes so parent can refresh
  disabled = false,
}) {
  const [featured, setFeatured] = useState(!!featuredOnHome);
  const [savingFeatured, setSavingFeatured] = useState(false);

  // Keep the toggle in sync when the parent finishes loading its data.
  useEffect(() => { setFeatured(!!featuredOnHome); }, [featuredOnHome]);

  const uploadFlyer = async (file) => {
    await eventMediaAPI.uploadFlyer(kind, eventId, file);
    onChange?.();
  };
  const removeFlyer = async () => {
    await eventMediaAPI.deleteFlyer(kind, eventId);
    onChange?.();
  };

  const toggleFeatured = async () => {
    const next = !featured;
    setFeatured(next);            // optimistic
    setSavingFeatured(true);
    try {
      await eventMediaAPI.setFeatured(kind, eventId, next);
      toast.success(next ? 'Featured on Home.' : 'Removed from Home.');
      onChange?.();
    } catch {
      setFeatured(!next);         // revert
      toast.error('Could not update Home feature.');
    } finally {
      setSavingFeatured(false);
    }
  };

  return (
    <div className="border border-[#e3dcd1] rounded-2xl bg-white p-4 space-y-4" data-testid="event-media-card">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Flyer &amp; Photos</div>
        <button
          type="button"
          onClick={toggleFeatured}
          disabled={disabled || savingFeatured}
          data-testid="event-feature-toggle"
          aria-pressed={featured}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs min-h-[40px] border transition-colors disabled:opacity-50 ${
            featured
              ? 'bg-[#c9a93a] text-[#013523] border-[#c9a93a] font-semibold'
              : 'bg-white text-[#6a766e] border-[#e3dcd1] hover:bg-[#f7f4ed]'
          }`}
        >
          <Star size={14} weight={featured ? 'fill' : 'regular'} />
          {featured ? 'Featured on Home' : 'Feature on Home'}
        </button>
      </div>

      <EventFlyerUpload
        flyerUrl={flyerUrl}
        onUpload={uploadFlyer}
        onRemove={removeFlyer}
        eventName={eventName}
        disabled={disabled}
      />

      <div className="pt-2 border-t border-[#efe9dd]">
        <EventPhotoManager kind={kind} eventId={eventId} disabled={disabled} />
      </div>
    </div>
  );
}
